import axios from 'axios';
import { Message } from 'element-ui';
import router from '@/router';
import store from '@/store';

import {
  getAccessProactiveRefreshAt,
  getAccessToken,
  getRefreshToken,
  setAccessProactiveRefreshAtFromExpiresIn,
  setAccessToken,
  setRefreshToken
} from '@/utils/auth';
import {
  armProactiveAccessRefreshTimer,
  clearProactiveAccessRefreshTimer
} from '@/utils/tokenRefreshSchedule';

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
});

function isPublicRequest(url) {
  if (!url) return false;
  return (
    url.includes('/v2/mpauth/school_admin_password_login') ||
    url.includes('/v2/mpauth/school_admin_reset_password') ||
    url.includes('/v2/MpAuth/sendSmsCode') ||
    url.includes('/v2/mpauth/send_sms_code') ||
    url.includes('/v2/common/agreement_list') ||
    url.includes('/v2/common/platform_config') ||
    url.includes('/v2/common/platformConfig')
  );
}

function isRefreshRequest(url) {
  return url && url.includes('/v2/token/refresh');
}

/** 业务体中表示 refresh 不可再用的 code（可按后端约定追加） */
function isRefreshRejectedBusinessCode(code) {
  if (code === undefined || code === null) return false;
  const n = Number(code);
  return n === 401 || n === 403;
}

/** HTTP 200 但业务体表示 access 不可用，需走刷新（与 HTTP 401 一致） */
function isTokenInvalidBusinessResponse(res) {
  if (!res || typeof res !== 'object') return false;
  const c = Number(res.code);
  if (c === 401 || c === 10001 || c === 10002) return true;
  const msg = pickResponseMessage(res);
  if (!msg) return false;
  if (msg.includes('令牌')) return true;
  return /token/i.test(msg) && /(无效|过期|失效|invalid|expired)/i.test(msg);
}

let isRefreshing = false;
let failedQueue = [];

function processQueue(error) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve();
  });
  failedQueue = [];
}

function pickResponseMessage(data) {
  if (!data || typeof data !== 'object') return '';
  return data.msg || data.message || '';
}

function forceLogoutAfterRefreshInvalid(message, queueError) {
  clearProactiveAccessRefreshTimer();
  processQueue(queueError || new Error(message || 'refresh invalid'));
  store.dispatch('logout');
  router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } });
  Message.error(message || '登录已过期，请重新登录');
}

/**
 * 调刷新接口并落库；成功后按新的 expires_in 安排下次无感刷新
 */
export async function execTokenRefresh() {
  const fd = new FormData();
  const data = await service.post('/v2/token/refresh', fd);
  const newAccess = data && (data.access_token || data.accessToken);
  if (!newAccess) throw new Error('刷新失败');
  setAccessToken(newAccess);
  const newRt = data.refresh_token || data.refreshToken;
  if (newRt) setRefreshToken(newRt);
  const rt = newRt || getRefreshToken();
  store.commit('SET_TOKENS', { accessToken: newAccess, refreshToken: rt });
  const exp = data.expires_in ?? data.expiresIn;
  setAccessProactiveRefreshAtFromExpiresIn(exp != null ? Number(exp) : undefined);
  scheduleNextProactiveRefresh();
  return data;
}

/** 根据本地记录的「应刷新时刻」挂一次定时器（与请求拦截器中的主动刷新互补） */
export function scheduleNextProactiveRefresh() {
  clearProactiveAccessRefreshTimer();
  const rt = getRefreshToken();
  const at = getAccessProactiveRefreshAt();
  if (!rt || !at) return;
  const delayMs = Math.max(0, at - Date.now());
  armProactiveAccessRefreshTimer(delayMs, () => {
    void (async () => {
      if (!getRefreshToken()) return;
      if (Date.now() < getAccessProactiveRefreshAt()) return;
      if (isRefreshing) return;
      isRefreshing = true;
      try {
        await execTokenRefresh();
        processQueue(null);
      } catch (e) {
        const st = e && e.response && e.response.status;
        const biz = e && e.response && e.response.data && e.response.data.code;
        if (st === 401 || st === 403 || isRefreshRejectedBusinessCode(biz)) {
          forceLogoutAfterRefreshInvalid(pickResponseMessage(e.response && e.response.data), e);
        } else {
          processQueue(e);
        }
      } finally {
        isRefreshing = false;
      }
    })();
  });
}

async function enqueueWhileRefreshing() {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });
}

/** 业务体判定令牌无效后：刷新 access 并重放原请求（与 HTTP 401 分支共用队列） */
async function retryOriginalAfterRefresh(originalConfig) {
  if (isRefreshing) {
    try {
      await enqueueWhileRefreshing();
      return service(originalConfig);
    } catch (queueErr) {
      return Promise.reject(queueErr);
    }
  }
  isRefreshing = true;
  try {
    await execTokenRefresh();
    processQueue(null);
    return service(originalConfig);
  } catch (refreshErr) {
    processQueue(refreshErr);
    const st = refreshErr && refreshErr.response && refreshErr.response.status;
    const biz =
      refreshErr &&
      refreshErr.response &&
      refreshErr.response.data &&
      refreshErr.response.data.code;
    if (st === 401 || st === 403 || isRefreshRejectedBusinessCode(biz)) {
      forceLogoutAfterRefreshInvalid(
        pickResponseMessage(refreshErr.response && refreshErr.response.data) || '登录已过期，请重新登录',
        refreshErr
      );
    } else {
      Message.error(
        pickResponseMessage(refreshErr.response && refreshErr.response.data) || '网络错误，请稍后重试'
      );
    }
    return Promise.reject(refreshErr);
  } finally {
    isRefreshing = false;
  }
}

async function ensureAccessTokenFreshIfNeeded(config) {
  const url = config.url || '';
  if (isPublicRequest(url) || isRefreshRequest(url)) return;
  if (!getRefreshToken()) return;
  const at = getAccessProactiveRefreshAt();
  if (!at || Date.now() < at) return;

  if (isRefreshing) {
    await enqueueWhileRefreshing();
    return;
  }

  isRefreshing = true;
  try {
    await execTokenRefresh();
    processQueue(null);
  } catch (e) {
    const st = e && e.response && e.response.status;
    const biz = e && e.response && e.response.data && e.response.data.code;
    if (st === 401 || st === 403 || isRefreshRejectedBusinessCode(biz)) {
      forceLogoutAfterRefreshInvalid(pickResponseMessage(e.response && e.response.data), e);
    } else {
      processQueue(e);
    }
    throw e;
  } finally {
    isRefreshing = false;
  }
}

service.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};
    const url = config.url || '';

    if (isRefreshRequest(url)) {
      const rt = getRefreshToken();
      if (rt) config.headers.token = rt;
      else delete config.headers.token;
      return config;
    }

    if (isPublicRequest(url)) {
      return config;
    }

    try {
      await ensureAccessTokenFreshIfNeeded(config);
    } catch {
      return Promise.reject(new Error('access refresh failed'));
    }

    const access = getAccessToken();
    if (access) config.headers.token = access;
    else delete config.headers.token;

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

service.interceptors.response.use(
  (response) => {
    const res = response.data;
    if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'code')) {
      const code = res.code;
      if (code !== undefined && code !== 0 && code !== 200 && code !== 1) {
        const msg = pickResponseMessage(res) || '请求失败';
        const reqUrl = (response.config && response.config.url) || '';
        const cfg = response.config || {};

        if (
          !isRefreshRequest(reqUrl) &&
          getRefreshToken() &&
          isTokenInvalidBusinessResponse(res) &&
          !cfg._bizTokenRetried
        ) {
          cfg._bizTokenRetried = true;
          return retryOriginalAfterRefresh(cfg);
        }

        if (!isRefreshRequest(reqUrl) && !cfg.skipErrorMessage) {
          Message.error(msg);
        }
        const err = new Error(msg);
        err.response = { status: 200, data: res };
        return Promise.reject(err);
      }
      return res.data !== undefined ? res.data : res;
    }
    return res;
  },
  async (error) => {
    const res = error.response;
    const status = res && res.status;
    const originalRequest = error.config;
    const url = (originalRequest && originalRequest.url) || '';

    if (originalRequest && isRefreshRequest(url) && (status === 401 || status === 403)) {
      isRefreshing = false;
      forceLogoutAfterRefreshInvalid(pickResponseMessage(res && res.data), error);
      return Promise.reject(error);
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      if (!getRefreshToken()) {
        clearProactiveAccessRefreshTimer();
        store.dispatch('logout');
        router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } });
        Message.error(pickResponseMessage(res && res.data) || '请先登录');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        try {
          await enqueueWhileRefreshing();
          return service(originalRequest);
        } catch (queueErr) {
          return Promise.reject(queueErr);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await execTokenRefresh();
        processQueue(null);
        return service(originalRequest);
      } catch (refreshErr) {
        const st = refreshErr && refreshErr.response && refreshErr.response.status;
        const biz =
          refreshErr &&
          refreshErr.response &&
          refreshErr.response.data &&
          refreshErr.response.data.code;
        if (st === 401 || st === 403 || isRefreshRejectedBusinessCode(biz)) {
          forceLogoutAfterRefreshInvalid(
            pickResponseMessage(refreshErr.response && refreshErr.response.data) ||
              '登录已过期，请重新登录',
            refreshErr
          );
        } else {
          processQueue(refreshErr);
          Message.error(
            pickResponseMessage(refreshErr.response && refreshErr.response.data) ||
              '网络错误，请稍后重试'
          );
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    if (status === 401) {
      clearProactiveAccessRefreshTimer();
      store.dispatch('logout');
      router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } });
      Message.error(pickResponseMessage(res && res.data) || '请先登录');
      return Promise.reject(error);
    }

    const msg =
      pickResponseMessage(res && res.data) ||
      error.message ||
      '网络错误';
    Message.error(msg);
    return Promise.reject(error);
  }
);

export default service;
